import { useState } from 'react';
import ExecutionContext from '../../ExecutionContext';
import { toUrl } from '../Utils/utils';
import { MessageBroker } from '~/Features/Messaging/MessageBroker';
import { useMessageHandler } from '~/Features/Messaging/useMessageHandler';
import { QueryParametersChanged } from '~/Partials/SearchBase/Events/QueryParametersChanged';

export type QueryParameter = { name: string; value: string };

export const formatQueryParameters = (params: QueryParameter[]) =>
    params.length === 0 ? '' : `?${params.map(param => `${param.name}=${param.value}`).join('&')}`;

export const getQueryParametersFromUrl = (url: string): QueryParameter[] => {
    const result = [] as QueryParameter[];

    if (ExecutionContext.IsServerSide()) {
        return result;
    }

    const params = toUrl(url).searchParams;
    params.forEach((value, key) => result.push({ name: key, value }));

    return result;
};

export const buildAbsoluteUrlWithQueryParameters = (baseUrl: string, queryParams: QueryParameter[]) => {
    if (ExecutionContext.IsServerSide() || queryParams.length === 0) {
        return baseUrl;
    }

    const newUrl = toUrl(baseUrl);
    newUrl.search = '';

    queryParams.forEach(param => newUrl.searchParams.append(param.name, param.value));

    return newUrl.toString();
};

export const changeQueryParametersInActiveUrl = (queryParameters: QueryParameter[], addHistoryEntry = true) => {
    ExecutionContext.ThrowIfServerSide();

    const urlWithSearchParams = buildAbsoluteUrlWithQueryParameters(window.location.pathname, queryParameters);

    if (addHistoryEntry) {
        document.URL !== urlWithSearchParams && window.history.pushState('', '', urlWithSearchParams);
    } else {
        window.history.replaceState('', '', urlWithSearchParams);
    }

    MessageBroker.publish(new QueryParametersChanged(queryParameters, addHistoryEntry));
};

export const useQueryParametersObserver = (onlyStoredInHistory = true) => {
    const [queryParams, setQueryParams] = useState<QueryParameter[]>([]);

    useMessageHandler(QueryParametersChanged.id, (event: QueryParametersChanged) => {
        if (onlyStoredInHistory && !event.storedInHistory) {
            return;
        }

        setQueryParams(event.queryParams);
    });

    return queryParams;
};
